import { Button } from "@/components/ui/button";
import { MapPin, ArrowRight, Star, CheckCircle2, ChevronDown, Calendar, Zap } from "lucide-react";
import { useLocation, REGIONS } from "@/lib/locations";
import { getLocalContent } from "@/lib/city-content";
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { PricingCalculator } from "@/components/PricingCalculator";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const BOOKING_URL = "/book";

export const Hero = () => {
  const location = useLocation();
  const localContent = getLocalContent(location.name, location.regionSlug);
  const [cleanersCount, setCleanersCount] = useState(3);

  useEffect(() => {
    const day = new Date().getDate();
    setCleanersCount((day % 5) + 2);
  }, []);
  
  return (
    <div className="relative min-h-[600px] lg:min-h-[700px] flex items-center overflow-hidden bg-background">
      {/* Background Image with Gradient Overlay */}
      <div className="absolute inset-0 z-0 bg-background">
        <img
          srcSet="https://vibe.filesafe.space/1776798830316075366/assets/e87a8deb-ec6f-458c-876c-a5573e164d40.png?w=800&q=80 800w, https://vibe.filesafe.space/1776798830316075366/assets/e87a8deb-ec6f-458c-876c-a5573e164d40.png?w=1920&q=80 1920w"
          sizes="(max-width: 768px) 800px, 1920px"
          src="https://vibe.filesafe.space/1776798830316075366/assets/e87a8deb-ec6f-458c-876c-a5573e164d40.png?w=1920&q=80"
          alt="Professional home cleaning service in action"
          className="h-full w-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          width="1920"
          height="700"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
      </div>

      <div className="container relative z-10 pt-36 pb-12 lg:pt-48 lg:pb-24">
        <div className="max-w-2xl">
          <div className="flex flex-wrap items-center gap-3 mb-6 anim-fade-up is-visible delay-100">
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground hover:bg-primary-hover transition-colors outline-none font-bold text-sm shadow-md">
                    <MapPin className="h-4 w-4" /> 
                    {REGIONS[location.regionSlug].name}
                    <ChevronDown className="h-4 w-4 opacity-70" />
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="top" className="font-medium">
                  <p>Click to change your service area</p>
                </TooltipContent>
              </Tooltip>
                <DropdownMenuContent align="start" className="w-56 p-2 max-h-80 overflow-y-auto">
                  <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider">California</DropdownMenuLabel>
                  {Object.entries(REGIONS).filter(([slug]) => slug !== 'phoenix-area').map(([slug, region]) => (
                    <DropdownMenuItem key={slug} asChild>
                      <Link to={`/locations/${slug}`} className="font-medium cursor-pointer">{region.name}</Link>
                    </DropdownMenuItem>
                  ))}
                  <div className="h-px bg-border my-1" />
                  <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wider">Arizona</DropdownMenuLabel>
                  {Object.entries(REGIONS).filter(([slug]) => slug === 'phoenix-area').map(([slug, region]) => (
                    <DropdownMenuItem key={slug} asChild>
                      <Link to={`/locations/${slug}`} className="font-medium cursor-pointer">{region.name}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
            </DropdownMenu>

            <div className="flex items-center gap-2 bg-background/60 px-3 py-1.5 rounded-full backdrop-blur-md border border-border/50">
              <div className="flex text-yellow-400" role="img" aria-label="5 stars">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <span className="text-sm font-medium text-foreground">Top-rated in {location.name}</span>
            </div>


          </div>

          <h1 className="text-4xl sm:text-5xl md:text-7xl font-extrabold tracking-tight mb-6 text-foreground leading-[1.1] anim-fade-up is-visible delay-200">
            House Cleaning in {location.name}.
          </h1>
          <p className="text-xl md:text-2xl text-foreground mb-2 max-w-lg font-semibold anim-fade-up is-visible delay-300">
            Get your exact price & book in 60 seconds
          </p>
          <p className="text-base md:text-lg text-muted-foreground mb-2 max-w-lg font-medium anim-fade-up is-visible delay-400">
            No hidden fees. Upfront pricing. 200% Satisfaction guaranteed
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4 anim-fade-up is-visible delay-500">
            <Button size="lg" className="h-14 px-8 text-lg font-bold rounded-full shadow-elegant hover:shadow-lg transition-all hover:-translate-y-1" asChild>
              <Link to={BOOKING_URL}>
                Get Instant Price <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="h-14 px-8 text-lg font-bold rounded-full bg-background/50 backdrop-blur-md border-border/50 hover:bg-background/80" asChild>
              <Link to="/services">
                View Services
              </Link>
            </Button>
          </div>

          <div className="mt-4 flex flex-col gap-1.5 anim-fade-up is-visible delay-600">
            <p className="text-[11px] text-muted-foreground">
              By booking, you agree to our <a href="https://www.uplevelcleaning.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary transition-colors">Terms of Service</a>, <a href="https://www.uplevelcleaning.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary transition-colors">Privacy Policy</a>, and <a href="https://www.uplevelcleaning.com/cleaning-policies" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary transition-colors">Cleaning Policies</a>.
            </p>
          </div>

          {/* Real-time cleaner availability widget */}
          <div className="mt-8 bg-background/90 backdrop-blur-xl border border-border/50 rounded-2xl p-4 shadow-elegant max-w-md anim-fade-up is-visible delay-700">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                </span>
                <span className="font-bold text-foreground text-xs uppercase tracking-wider">Live Availability</span>
              </div>
              <span className="text-xs font-semibold text-muted-foreground bg-muted/80 px-2.5 py-1 rounded-md">{location.name}</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary shrink-0">
                {cleanersCount}
              </div>
              <div>
                <p className="text-sm font-bold text-foreground">
                  {cleanersCount} Cleaners in your area
                </p>
                <p className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                  Next opening: <span className="text-primary font-bold">{new Date().getHours() < 14 ? "Today" : "Tomorrow"}</span>
                </p>
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex flex-wrap items-center gap-4 text-sm font-medium text-muted-foreground anim-fade-up is-visible delay-600">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> No commitment</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> Cancel anytime</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> Insured &amp; bonded</span>
          </div>
        </div>
      </div>
    </div>
  );
};
